import React, { useEffect, useRef } from 'react';
import socket from "../../socket";
import { useChat } from '../../hooks/useChat';
import {
  Box,
  Typography,
  Avatar,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Fab
} from '@mui/material';
import {
  MoreVert as MoreIcon,
  Delete as DeleteIcon,
  Reply as ReplyIcon,
  Edit as EditIcon,
  Videocam as VideoIcon,
  Call as CallIcon,
  ScreenShare as ScreenShareIcon,
  InfoOutlined as InfoIcon,
  LockOutlined as LockIcon,
  PersonAdd as PersonAddIcon,
  CallEnd as CallEndIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import MessageInput from './MessageInput';
import CallWindow from './CallWindow';

const ChatWindow = ({
  activeChannel,
  messages,
  members,
  loading,
  replyTo,
  setReplyTo,
  sendMessage,
  deleteMessage,
  deleteChannel,
  uploadAttachment,
  currentUser,
  selectChannel,
  isMobile
}) => {
  const messagesEndRef = useRef(null);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [selectedMsg, setSelectedMsg] = React.useState(null);
  const [callActive, setCallActive] = React.useState(false);
  const [callType, setCallType] = React.useState(null); // 'audio' or 'video'
  const [isCaller, setIsCaller] = React.useState(false);
  const [ringingCall, setRingingCall] = React.useState(null); // { from, name, type }

  // Add Member State
  const [addMemberOpen, setAddMemberOpen] = React.useState(false);
  const [allUsers, setAllUsers] = React.useState([]);
  const [loadingUsers, setLoadingUsers] = React.useState(false);
  //const { fetchUsers, addMember } = require('../../hooks/useChat').useChat();
const { fetchUsers, addMember } = useChat();

  const handleOpenAddMember = async () => {
    setAddMemberOpen(true);
    setLoadingUsers(true);
    const users = await fetchUsers();
    setAllUsers(users);
    setLoadingUsers(false);
  };

  const handleAddUser = async (user_id) => {
    try {
      await addMember(activeChannel.id, user_id);
      setAddMemberOpen(false);
    } catch (err) {
      alert("Failed to add member: " + err.message);
    }
  };

  const startCall = (type) => {
    console.log("Call initiated:", type);
    // Notify others in the room
    socket.emit("call-initiate", {
      channel: activeChannel.id,
      from: currentUser.id,
      callerName: currentUser.name || "User",
      type: type
    });
    setCallType(type);
    setIsCaller(true);
    setCallActive(true);
  };

  const endCall = () => {
    socket.emit("end-call", { channel: activeChannel?.id });
    setCallActive(false);
    setCallType(null);
    setIsCaller(false);
  };
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (activeChannel) {
      socket.emit("join-channel", activeChannel.id);
    }

    const handleIncomingCall = (data) => {
      if (data.from !== currentUser?.id) {
        setRingingCall({
           from: data.from,
           name: data.callerName,
           type: data.type,
           channelId: data.channel
        });
      }
    };

    socket.on("incoming-call", handleIncomingCall);

    return () => {
      socket.off("incoming-call", handleIncomingCall);
    };
  }, [activeChannel, currentUser]);

  const handleAcceptCall = () => {
      if (ringingCall) {
          setCallType(ringingCall.type);
          setIsCaller(false);
          setCallActive(true);
          
          // Delay to ensure CallWindow mounts and attaches socket listeners before caller creates offer
          setTimeout(() => {
              socket.emit("call-response", { channel: ringingCall.channelId, accepted: true });
          }, 500);
          
          setRingingCall(null);
      }
  };

  const handleRejectCall = () => {
      if (ringingCall) {
          socket.emit("call-response", { channel: ringingCall.channelId, accepted: false });
          socket.emit("end-call", { channel: ringingCall.channelId });
          setRingingCall(null);
      }
  };


  const handleDelete = () => {
    if (selectedMsg) {
      deleteMessage(selectedMsg.id);
    }
    setAnchorEl(null);
    setSelectedMsg(null);
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return date.toLocaleDateString(undefined, {
      weekday: 'long',
      month: 'short',
      day: 'numeric'
    });
  };

  // Group messages by date
  const groupedMessages = messages.reduce((groups, msg) => {
    const date = new Date(msg.created_at).toDateString();
    if (!groups[date]) groups[date] = [];
    groups[date].push(msg);
    return groups;
  }, {});

  if (!activeChannel) {
    return (
      <Box className="chat-empty">
        <Box className="empty-state">
          <Typography variant="h5" color="textSecondary" gutterBottom>
            Welcome to Team Chat
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Select a channel to start messaging
          </Typography>
          <Box className="empty-hint">
            <Typography variant="caption">
              💡 Tip: Use #general for team-wide announcements
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box className="chat-window">
      {/* Header */}
      <Box
        sx={{
          p: isMobile ? '8px 12px' : '12px 24px',
          borderBottom: '1px solid #edebe9',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: '#fff'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isMobile && (
            <IconButton size="small" onClick={() => selectChannel(null)} sx={{ mr: 0.5 }}>
              <ArrowBackIcon sx={{ fontSize: 20 }} />
            </IconButton>
          )}
          <Avatar
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1,
              bgcolor: '#4b53bc',
              color: '#ffffff',
              fontSize: 15,
              fontWeight: 700
            }}
          >
            {activeChannel.name.charAt(0).toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#242424', display: 'flex', alignItems: 'center', gap: 1, fontSize: '15px' }}>
              #{activeChannel.name}
              {activeChannel.type === 'private' && <LockIcon sx={{ fontSize: 14, color: '#616161' }} />}
            </Typography>
            {activeChannel.description && (
              <Typography variant="caption" sx={{ color: '#616161', display: 'block', maxWidth: 300 }} noWrap>
                {activeChannel.description}
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title="Start Video Call">
            <IconButton size="small" onClick={() => startCall('video')} sx={{ color: '#4b53bc', '&:hover': { bgcolor: 'rgba(75, 83, 188, 0.08)' } }}>
              <VideoIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Start Audio Call">
            <IconButton size="small" onClick={() => startCall('audio')} sx={{ color: '#4b53bc', '&:hover': { bgcolor: 'rgba(75, 83, 188, 0.08)' } }}>
              <CallIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Share Screen / Present">
            <IconButton size="small" onClick={() => startCall('video')} sx={{ color: '#4b53bc', '&:hover': { bgcolor: 'rgba(75, 83, 188, 0.08)' } }}>
              <ScreenShareIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1 }} />
          <Tooltip title="Add People to Channel">
            <IconButton size="small" onClick={handleOpenAddMember} sx={{ color: '#64748b' }}>
              <PersonAddIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          {((activeChannel?.created_by === currentUser?.id) || currentUser?.role_name === 'Admin' || currentUser?.role_id == 1) && (
            <Tooltip title="Delete Channel / Group">
              <IconButton
                size="small"
                onClick={() => {
                  if (window.confirm(`Are you sure you want to permanently delete #${activeChannel.name}? All messages and attachments will be deleted.`)) {
                    deleteChannel(activeChannel.id);
                  }
                }}
                sx={{ color: '#ef4444', '&:hover': { bgcolor: '#fef2f2' } }}
              >
                <DeleteIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Active Call Window */}
      {callActive && (
        <CallWindow
          activeChannel={activeChannel}
          callType={callType}
          onHangUp={endCall}
          currentUser={currentUser}
          isCaller={isCaller}
        />
      )}

      {/* Messages */}
      <Box className="messages-container">
        {loading ? (
          <Box className="loading-state">
            <Typography color="textSecondary">Loading messages...</Typography>
          </Box>
        ) : !messages || messages.length === 0 ? (
          <Box className="no-messages">
            <Typography color="textSecondary">
              No messages yet. Start the conversation!
            </Typography>
          </Box>
        ) : (
          Object.entries(groupedMessages).map(([date, msgs]) => (
            <Box key={date} className="message-group">
              <Box className="date-divider">
                <Typography variant="caption">{formatDate(date)}</Typography>
              </Box>

              {msgs.map((msg, index) => {
                const isOwn = msg.user_id === currentUser?.id;
                const showAvatar = index === 0 || msgs[index - 1].user_id !== msg.user_id;
                const userName = msg.user_name || 'User';

                return (
                  <Paper
                    key={msg.id}
                    className={`message ${isOwn ? 'own' : ''} ${msg.is_deleted ? 'deleted' : ''}`}
                    elevation={0}
                  >
                    {/* Reply indicator */}
                    {msg.parent_id && (
                      <Box className="reply-indicator">
                        <ReplyIcon fontSize="small" />
                        <Typography variant="caption">
                          Replying to earlier message
                        </Typography>
                      </Box>
                    )}

                    <Box className="message-wrapper">
                      {showAvatar && !isOwn ? (
                        <Avatar
                          className="message-avatar"
                          sx={{
                            bgcolor: stringToColor(userName),
                            width: 36,
                            height: 36
                          }}
                        >
                          {userName.charAt(0).toUpperCase()}
                        </Avatar>
                      ) : !isOwn ? (
                        <Box className="avatar-placeholder" />
                      ) : null}

                      <Box className={`message-content ${!showAvatar && !isOwn ? 'indented' : ''}`}>
                        {showAvatar && (
                          <Box className="message-header">
                            <Typography variant="subtitle2" className="username">
                              {userName}
                            </Typography>
                            <Typography variant="caption" className="timestamp">
                              {formatTime(msg.created_at)}
                            </Typography>
                          </Box>
                        )}

                        <Typography variant="body1" className="message-text">
                          {msg.content}
                        </Typography>

                        {/* Attachments */}
                        {msg.file && (
  <Box className="attachment">
    <Paper className="attachment-card" elevation={1}>
      <Box className="attachment-icon">
        📎
      </Box>
      <Box className="attachment-info">
        <Typography variant="body2" className="filename">
          Attachment
        </Typography>

        <a
          href={`http://localhost/x2infotech_full_integrated_project/${msg.file}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          Open File
        </a>
      </Box>
    </Paper>
  </Box>
)}
                        

                        

                        {/* Message actions */}
                        <IconButton
                          size="small"
                          className="message-menu-btn"
                          onClick={(e) => {
                            setAnchorEl(e.currentTarget);
                            setSelectedMsg(msg);
                          }}
                        >
                          <MoreIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          ))
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Reply Preview */}
      {replyTo && (
        <Box className="reply-bar">
          <Box className="reply-info">
            <ReplyIcon fontSize="small" />
            <Typography variant="caption">
              Replying to <strong>{replyTo.user_name}</strong>
            </Typography>
            <Typography variant="caption" className="reply-preview-text" noWrap>
              {replyTo.content}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setReplyTo(null)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      {/* Input */}
      <Box sx={{ p: '12px 24px', pb: '30px', bgcolor: '#fff', borderTop: '1px solid #edebe9' }}>
        <MessageInput
          onSend={sendMessage}
          onUpload={uploadAttachment}
          replyTo={replyTo}
          setReplyTo={setReplyTo}
          disabled={!activeChannel}
        />
      </Box>

      {/* Message Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        PaperProps={{ className: 'message-menu' }}
      >
        <MenuItem onClick={() => {
          setReplyTo(selectedMsg);
          setAnchorEl(null);
        }}>
          <ReplyIcon fontSize="small" sx={{ mr: 1 }} /> Reply
        </MenuItem>
        {(selectedMsg?.user_id === currentUser?.id || currentUser?.role_name === 'Admin' || currentUser?.role_id == 1) && (
          <MenuItem
            onClick={handleDelete}
            className="delete-item"
            sx={{ color: '#ef4444', fontWeight: 600 }}
          >
            <DeleteIcon fontSize="small" sx={{ mr: 1, color: '#ef4444' }} /> Delete Message
          </MenuItem>
        )}
      </Menu>

      {/* Add Member Dialog */}
      <Dialog open={addMemberOpen} onClose={() => setAddMemberOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Add people to {activeChannel.name}</DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {loadingUsers ? (
            <Box sx={{ p: 3, textAlign: 'center' }}><Typography color="textSecondary">Loading users...</Typography></Box>
          ) : (
            <List>
              {allUsers.map(u => {
                if (u.id === currentUser?.id) return null;
                return (
                  <ListItem key={u.id} button onClick={() => handleAddUser(u.id)}>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: '#4b53bc', width: 34, height: 34, fontSize: '14px', fontWeight: 700 }}>
                        {u.name.charAt(0).toUpperCase()}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={<Typography variant="body2" sx={{ fontWeight: 600 }}>{u.name}</Typography>}
                      secondary={u.department || u.role_name || u.email}
                    />
                    <Button variant="outlined" size="small" sx={{ textTransform: 'none', color: '#4b53bc', borderColor: '#4b53bc', fontWeight: 600 }}>
                      Add
                    </Button>
                  </ListItem>
                );
              })}
              {allUsers.length === 0 && (
                <Box sx={{ p: 3, textAlign: 'center' }}><Typography color="textSecondary">No users found.</Typography></Box>
              )}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddMemberOpen(false)} sx={{ textTransform: 'none', color: '#616161' }}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Incoming Call Ringing Dialog */}
      <Dialog 
        open={Boolean(ringingCall)} 
        maxWidth="xs" 
        PaperProps={{
            style: { borderRadius: 16, padding: '20px', textAlign: 'center', boxShadow: '0 8px 30px rgba(0,0,0,0.2)' }
        }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 700, color: '#242424' }}>
            Incoming {ringingCall?.type} Call
        </DialogTitle>
        <DialogContent sx={{ pb: 3 }}>
            <Avatar 
                sx={{
                    width: 72,
                    height: 72,
                    margin: '0 auto 16px',
                    bgcolor: '#4b53bc',
                    fontSize: '28px',
                    fontWeight: 700,
                    boxShadow: '0 4px 16px rgba(75, 83, 188, 0.3)'
                }}
            >
                {ringingCall?.name?.charAt(0).toUpperCase() || '?'}
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#242424' }}>{ringingCall?.name}</Typography>
            <Typography variant="body2" sx={{ color: '#616161', mt: 0.5 }}>is calling you in #{activeChannel?.name}</Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 3, pb: 2 }}>
            <Fab color="error" onClick={handleRejectCall} sx={{ width: 56, height: 56, bgcolor: '#d9383a' }}>
                <CallEndIcon />
            </Fab>
            <Fab
                onClick={handleAcceptCall}
                sx={{
                    width: 56,
                    height: 56,
                    bgcolor: '#4b53bc',
                    color: '#fff',
                    boxShadow: '0 4px 14px rgba(75, 83, 188, 0.4)',
                    '&:hover': { bgcolor: '#3b43a2' }
                }}
            >
                {ringingCall?.type === 'video' ? <VideoIcon /> : <CallIcon />}
            </Fab>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Helper to generate consistent colors for usernames
function stringToColor(string) {
  if (!string) return '#4caf50';
  let hash = 0;
  for (let i = 0; i < string.length; i++) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }
  const c = (hash & 0x00FFFFFF).toString(16).toUpperCase();
  return '#' + '00000'.substring(0, 6 - c.length) + c;
}

function getFileIcon(type) {
  if (type?.startsWith('image/')) return '🖼️';
  if (type?.includes('pdf')) return '📄';
  if (type?.includes('word')) return '📝';
  if (type?.includes('excel') || type?.includes('sheet')) return '📊';
  return '📎';
}

export default ChatWindow;
